import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import {
  getSessionStatusDisplay,
  getSessionStatusTooltip,
  getStatusText,
  getStatusColor,
  getStatusTextColor,
  getStatusBorderColor
} from '../../../constants/feedbackSessionStatuses';

const tooltipConfig = {
  arrow: true,
  placement: 'top',
  enterDelay: 500,
  leaveDelay: 200
};

/**
 * ADR-014 Decision 10 — the single session status chip.
 *
 * The session lifecycle has four states; "closed early" is a property of the
 * terminal one, not a fifth state. Rather than stacking a second tag under a
 * green "Completed" chip (which read as success even when work was left
 * unfinished), the terminal chip itself carries the closure: "Completed" in
 * green when everything was finished, "Closed" in neutral slate when it was
 * not, with the reason and the completed/total counts in the tooltip.
 *
 * Keep the card label bare and pass `labelSuffix` on detail surfaces
 * ("Closed · 8/12 completed") — short where users scan, expanded where they
 * investigate.
 *
 * Every status carries a tooltip (ADR-014 §10i), not just the closed one: an
 * affordance that works on only one chip teaches users that chips are not worth
 * hovering, which buries the closure detail that matters most.
 *
 * Render this instead of hand-rolling getStatusText/getStatusColor calls, so
 * every surface stays consistent.
 *
 * @param {Object} session - the feedback session (needs completedAt/startedAt/
 *   publishedAt, and closureType/closureReason/closureSummary when available)
 * @param {string} [labelSuffix] - appended after a middot, e.g. "8/12 completed"
 * @param {string} [value] - force a FEEDBACK_SESSION_STATUS_FILTERS value instead
 *   of deriving it. For payloads that carry closureType but not the lifecycle
 *   timestamps (the session report header); the tooltip is still derived from
 *   `session`.
 * @param {Object} [sx] - MUI style overrides merged over the chip defaults
 */
export const SessionStatusChip = ({ session, labelSuffix = null, value = null, sx = {} }) => {
  const derived = getSessionStatusDisplay(session);
  const display = value
    ? {
      ...derived,
      value,
      label: getStatusText(value),
      backgroundColor: getStatusColor(value),
      textColor: getStatusTextColor(value),
      borderColor: getStatusBorderColor(value),
      // The tooltip must follow the override — a report payload with no
      // lifecycle timestamps would otherwise describe the derived NotStarted.
      tooltip: getSessionStatusTooltip(session, value)
    }
    : derived;

  const chip = (
    <Chip
      label={labelSuffix ? `${display.label} · ${labelSuffix}` : display.label}
      sx={{
        backgroundColor: display.backgroundColor,
        color: display.textColor,
        fontWeight: 500,
        fontSize: '13px',
        border: '1px solid',
        borderColor: display.borderColor,
        '& .MuiChip-label': {
          px: 2
        },
        ...sx
      }}
    />
  );

  if (!display.tooltip) {
    return chip;
  }

  return (
    <Tooltip title={display.tooltip} {...tooltipConfig}>
      {chip}
    </Tooltip>
  );
};

export default SessionStatusChip;
